import { useEffect, useCallback, useRef } from 'react';
import { ExternalLink, Download, AlertCircle } from 'lucide-react';
import { PDFViewer } from './PDFViewer';
import { VideoPlayer } from './VideoPlayer';
import { AudioPlayer } from './AudioPlayer';
import { ImageGallery } from './ImageGallery';
import { DocumentViewer } from './DocumentViewer';
import { CSVViewer } from './CSVViewer';
import { ViewerShell } from './ViewerShell';
import { useResourceProgress } from '@/hooks/useResourceProgress';
import { useToast } from '@/context/ToastContext';
import { useVisualEffectsContext } from '@/context/VisualEffectsContext';
import { formatCelebrationMessage } from '@/types/reading';
import { triggerDownload } from '@/utils/download';
import type { FileType } from '@/types/library';
import type { FavoriteCachedData } from '@/types/favorites';

function isCSVFile(fileName?: string, mimeType?: string, url?: string): boolean {
  if (mimeType === 'text/csv' || mimeType === 'application/csv') return true;
  if (fileName) {
    const ext = fileName.toLowerCase().slice(fileName.lastIndexOf('.'));
    if (ext === '.csv') return true;
  }
  if (url) {
    try {
      const urlPath = new URL(url).pathname.toLowerCase();
      const pathWithoutQuery = urlPath.split('?')[0];
      if (pathWithoutQuery.endsWith('.csv')) return true;
    } catch {
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.includes('.csv?') || lowerUrl.endsWith('.csv')) return true;
    }
  }
  return false;
}

interface UniversalViewerProps {
  url: string;
  type: FileType;
  title?: string;
  fileName?: string;
  mimeType?: string;
  onClose: () => void;
  onDownload?: () => void;
  resourceId?: string;
  fileId?: string;
  totalPages?: number;
  favoriteCachedData?: FavoriteCachedData;
}

export function UniversalViewer({
  url,
  type,
  title,
  fileName,
  mimeType,
  onClose,
  onDownload,
  resourceId,
  fileId,
  totalPages,
  favoriteCachedData,
}: UniversalViewerProps) {
  const { showToast } = useToast();
  const { triggerState } = useVisualEffectsContext();

  const celebrationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) clearTimeout(celebrationTimeoutRef.current);
    };
  }, []);

  const { reportMediaProgress, reportPageChange } = useResourceProgress({
    resourceId: resourceId || '',
    fileId: fileId || '',
    title: title || fileName || 'Resource',
    type,
    totalPages,
    enabled: !!(resourceId && fileId),
    callbacks: {
      onResourceComplete: (resourceTitle, isFirstResource) => {
        triggerState('celebration');
        showToast({
          message: formatCelebrationMessage('resourceComplete', { resource: resourceTitle }),
          type: 'success',
        });
        if (isFirstResource) {
          celebrationTimeoutRef.current = setTimeout(() => {
            showToast({ message: 'You completed your first study resource!', type: 'success' });
          }, 2000);
        }
      },
    },
  });

  const handlePdfPageChange = useCallback(
    (currentPage: number, pdfTotalPages: number) => {
      reportPageChange(currentPage, pdfTotalPages);
    },
    [reportPageChange]
  );

  const handleMediaProgress = useCallback(
    (progress: number) => {
      reportMediaProgress(progress);
    },
    [reportMediaProgress]
  );

  const handleDownload = () => {
    if (onDownload) onDownload();
    else triggerDownload(url, fileName || title || 'file');
  };

  switch (type) {
    case 'pdf':
      return (
        <PDFViewer
          url={url}
          title={title}
          onClose={onClose}
          onDownload={onDownload}
          onPageChange={handlePdfPageChange}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'video':
      return (
        <VideoPlayer
          url={url}
          title={title}
          fileName={fileName}
          mimeType={mimeType}
          onClose={onClose}
          onDownload={onDownload}
          onProgress={handleMediaProgress}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'audio':
      return (
        <AudioPlayer
          url={url}
          title={title}
          fileName={fileName}
          mimeType={mimeType}
          onClose={onClose}
          onDownload={onDownload}
          onProgress={handleMediaProgress}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'image':
      return (
        <ImageGallery
          images={[{ url, title: title || fileName }]}
          onClose={onClose}
          onDownload={onDownload}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'doc':
      if (isCSVFile(fileName, mimeType, url)) {
        return (
          <CSVViewer
            url={url}
            title={title}
            fileName={fileName}
            onClose={onClose}
            onDownload={onDownload}
            fileId={fileId}
            favoriteCachedData={favoriteCachedData}
          />
        );
      }
      return (
        <DocumentViewer
          url={url}
          title={title}
          fileName={fileName}
          onClose={onClose}
          onDownload={onDownload}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'link':
      return (
        <LinkViewer
          url={url}
          title={title}
          onClose={onClose}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );

    case 'other':
    default:
      if (isCSVFile(fileName, mimeType, url)) {
        return (
          <CSVViewer
            url={url}
            title={title}
            fileName={fileName}
            onClose={onClose}
            onDownload={onDownload}
            fileId={fileId}
            favoriteCachedData={favoriteCachedData}
          />
        );
      }
      return (
        <FallbackViewer
          url={url}
          title={title}
          onClose={onClose}
          onDownload={handleDownload}
          onOpenExternal={() => window.open(url, '_blank')}
          fileId={fileId}
          favoriteCachedData={favoriteCachedData}
        />
      );
  }
}

// ============================================================
// Link Viewer
// ============================================================

interface LinkViewerProps {
  url: string;
  title?: string;
  onClose: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

function LinkViewer({ url, title, onClose, fileId, favoriteCachedData }: LinkViewerProps) {
  const getEmbedUrl = (originalUrl: string): string | null => {
    try {
      const urlObj = new URL(originalUrl);
      if (urlObj.hostname.includes('youtube.com')) {
        const videoId = urlObj.searchParams.get('v');
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      }
      if (urlObj.hostname.includes('youtu.be')) {
        const videoId = urlObj.pathname.slice(1);
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      }
      if (urlObj.hostname.includes('vimeo.com')) {
        const videoId = urlObj.pathname.split('/').pop();
        if (videoId) return `https://player.vimeo.com/video/${videoId}`;
      }
      return null;
    } catch {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(url);

  if (embedUrl) {
    return (
      <ViewerShell
        title={title || 'Video'}
        type="link"
        onClose={onClose}
        fileId={fileId}
        favoriteCachedData={favoriteCachedData}
        keyboardHint="Esc to close"
      >
        <div className="flex-1 flex items-center justify-center p-2 sm:p-4 min-h-full">
          <div className="w-full max-w-4xl aspect-video">
            <iframe
              src={embedUrl}
              className="w-full h-full rounded-lg"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title={title || 'Video'}
            />
          </div>
        </div>
      </ViewerShell>
    );
  }

  return (
    <ViewerShell
      title={title || 'External Link'}
      type="link"
      onClose={onClose}
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      keyboardHint="Esc to close"
    >
      <div className="flex items-center justify-center min-h-full p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-[#f0d2f0]/30 max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-br from-[#f8e8f8] to-[#fcf4fc] p-6 text-center">
            <ExternalLink size={48} className="text-[#e5bce5] mx-auto mb-4" />
            <p className="text-[#5a3d58] text-sm mb-1">This link will open in a new browser tab.</p>
            <p className="text-[#8e4585]/50 text-xs truncate">{url}</p>
          </div>
          <div className="p-4 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                window.open(url, '_blank');
                onClose();
              }}
              className="px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ExternalLink size={16} /> Open Link
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#f8e8f8] text-[#5a3d58] rounded-lg hover:bg-[#f0d2f0] transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </ViewerShell>
  );
}

// ============================================================
// Fallback Viewer
// ============================================================

interface FallbackViewerProps {
  url: string;
  title?: string;
  onClose: () => void;
  onDownload: () => void;
  onOpenExternal: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

function FallbackViewer({
  url,
  title,
  onClose,
  onDownload,
  onOpenExternal,
  fileId,
  favoriteCachedData,
}: FallbackViewerProps) {
  return (
    <ViewerShell
      title={title || 'File'}
      type="other"
      onClose={onClose}
      onDownload={onDownload}
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      keyboardHint="Esc to close"
    >
      <div className="flex items-center justify-center min-h-full p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-[#f0d2f0]/30 max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-br from-[#f8e8f8] to-[#fcf4fc] p-6 text-center">
            <AlertCircle size={48} className="text-[#e5bce5] mx-auto mb-4" />
            <p className="text-[#5a3d58] text-sm mb-1">Preview not available for this file type.</p>
            <p className="text-[#8e4585]/50 text-xs truncate">{url}</p>
          </div>
          <div className="p-4 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onDownload}
              className="px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Download size={16} /> Download
            </button>
            <button
              onClick={onOpenExternal}
              className="px-4 py-2 bg-[#f8e8f8] text-[#5a3d58] rounded-lg hover:bg-[#f0d2f0] transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ExternalLink size={16} /> Open
            </button>
          </div>
        </div>
      </div>
    </ViewerShell>
  );
}
